import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Check, Copy, X } from "lucide-react";

import { BADGE_TONES } from "../../theme/badgeTones";
import { clipboardHtmlToMarkdown } from "../../utils/markdownClipboard";
import { Button } from "../Button";
import UnsavedChangesBar from "../UnsavedChangesBar";
import { CustomTextArea } from "../CustomTextArea";
import { EMPTY_PROMPT_PLACEHOLDER } from "./agencyPrompts";

export const PROMPT_FONT_FAMILY =
  'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace)';

const SAVED_FEEDBACK_MS = 2500;

const errorMessageOf = (error) => {
  if (error && !error.isAxiosError && error.message) return error.message;
  const data = error?.response?.data;
  const fieldError = data && typeof data === "object" ? Object.values(data).flat().find((v) => typeof v === "string") : null;
  return data?.detail || data?.error || fieldError || "No se pudo guardar el prompt.";
};

function CopyIconButton({ text }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? "Copiado" : "Copiar"} arrow>
      <IconButton size="small" onClick={handleCopy} aria-label={copied ? "Copiado" : "Copiar prompt"}>
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </IconButton>
    </Tooltip>
  );
}

CopyIconButton.propTypes = {
  text: PropTypes.string.isRequired,
};

/**
 * Prompt textarea, always editable unless `readOnly`. Guardar and Descartar only
 * appear once the text differs from the saved value; `onSave(text)` persists it.
 * Formatted text copied from an AI chat is pasted as Markdown.
 */
export function PromptField({
  id,
  value,
  onSave,
  readOnly = false,
  placeholder = EMPTY_PROMPT_PLACEHOLDER,
  onDirtyChange,
  ariaLabel,
  showCounts = false,
  autoExpand = false,
  rows = 8,
  maxRows,
  textareaSx,
  floatingSave = false,
}) {
  const theme = useTheme();
  const [draft, setDraft] = useState(value);
  const [baseline, setBaseline] = useState(value);
  const baselineRef = useRef(value);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const savedTimeoutRef = useRef(null);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  // A new saved value (save elsewhere, refetch) replaces the text unless it has unsaved edits.
  useEffect(() => {
    if (value === baselineRef.current) return;
    const previous = baselineRef.current;
    baselineRef.current = value;
    setBaseline(value);
    setDraft((current) => (current === previous ? value : current));
  }, [value]);

  const dirty = !readOnly && draft !== baseline;
  const saving = status === "saving";

  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);

  useEffect(
    () => () => {
      clearTimeout(savedTimeoutRef.current);
      onDirtyChangeRef.current?.(false);
    },
    []
  );

  const handleChange = (event) => {
    setDraft(event.target.value);
    setError(null);
    if (status === "saved") setStatus("idle");
  };

  const handleDiscard = () => {
    setDraft(baseline);
    setError(null);
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    const text = draft;
    setStatus("saving");
    setError(null);
    try {
      await onSave(text);
      baselineRef.current = text;
      setBaseline(text);
      setStatus("saved");
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = setTimeout(() => setStatus("idle"), SAVED_FEEDBACK_MS);
    } catch (saveError) {
      setStatus("idle");
      setError(errorMessageOf(saveError));
    }
  };

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
      event.preventDefault();
      handleSave();
    }
  };

  // Rich HTML (ChatGPT, Claude, Gemini, Docs) is pasted as Markdown; plain text pastes natively.
  const handlePaste = (event) => {
    if (readOnly) return;
    const markdown = clipboardHtmlToMarkdown(event.clipboardData);
    if (!markdown) return;
    event.preventDefault();
    const textarea = event.currentTarget;
    textarea.focus();
    // insertText keeps the browser's undo history and fires onChange with the caret after the paste.
    if (document.execCommand?.("insertText", false, markdown)) return;
    const { selectionStart, selectionEnd, value: current } = textarea;
    const caret = selectionStart + markdown.length;
    setDraft(current.slice(0, selectionStart) + markdown + current.slice(selectionEnd));
    setError(null);
    if (status === "saved") setStatus("idle");
    requestAnimationFrame(() => textarea.setSelectionRange(caret, caret));
  };

  const words = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0, flex: textareaSx?.flex ? 1 : undefined }}>
      <CustomTextArea
        id={id}
        aria-label={ariaLabel}
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={false}
        rows={rows}
        autoExpand={autoExpand}
        maxRows={maxRows}
        spellCheck={false}
        sx={{
          fontFamily: PROMPT_FONT_FAMILY,
          fontSize: "13px",
          lineHeight: 1.6,
          padding: "12px 14px",
          ...(readOnly && { backgroundColor: theme.palette.action.hover, cursor: "text" }),
          ...textareaSx,
        }}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 0 }} aria-live="polite">
          {error ? (
            <Typography role="alert" sx={{ fontSize: "0.75rem", color: theme.palette.error.main }}>
              {error}
            </Typography>
          ) : status === "saved" && !dirty ? (
            <Typography
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", fontWeight: 600, color: BADGE_TONES.done.color }}
            >
              <Check size={14} />
              Guardado
            </Typography>
          ) : showCounts ? (
            <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
              {words} palabras · {draft.length} caracteres
            </Typography>
          ) : null}
        </Box>
        {floatingSave ? (
          <>
            <UnsavedChangesBar count={dirty ? 1 : 0} saving={saving} onDiscard={handleDiscard} onSave={handleSave} />
            {draft && <CopyIconButton text={draft} />}
          </>
        ) : dirty ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Button variant="outline" size="xs" onClick={handleDiscard} disabled={saving}>
              <X size={14} />
              Descartar
            </Button>
            <Button size="xs" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <Check size={14} />}
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        ) : (
          draft && <CopyIconButton text={draft} />
        )}
      </Box>
    </Box>
  );
}

PromptField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  onDirtyChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  showCounts: PropTypes.bool,
  autoExpand: PropTypes.bool,
  rows: PropTypes.number,
  maxRows: PropTypes.number,
  textareaSx: PropTypes.object,
  // En página usa la barra flotante; dentro de un modal, los botones de siempre
  floatingSave: PropTypes.bool,
};
